<template>
  <div class="content">
    <h1>Bulk bank payment</h1>

    <div
      v-if="!uploaded"
      class="margin-bottom margin-top">
      <div class="border-black small-padding margin-bottom"><h3>Exported bank payment data</h3>
        <ul style="padding-left: 1em;">
          <li>The format is the one used when copy/pasting correctly from the bank payment sheet.</li>
          <li>There must be one order per line.</li>
          <li>The data points must be separated by semicolon.</li>
          <li>The order ID must be present, otherwise the order cannot be located.</li>
        </ul>
      </div>
      <textarea
        style="width: 500px; height: 200px;"
        placeholder="123456;399
123457;199
        "
        class="margin-bottom border-black"
        v-model="form.data"></textarea>
      <br/>
      <textarea
        style="width: 500px; height: 100px;"
        placeholder="Comment (Will be applied to all the orders)."
        class="margin-bottom border-black"
        v-model="form.comment"></textarea>
      <div>
        <button
          v-on:click="submit"
          :disabled="saving || !form.data">Upload</button>
      </div>
    </div>
    <loader v-if="saving"></loader>
    <div v-if="uploaded" class="margin-top">
      <div class="success small-padding text-center uppercase" style="position: relative">
        <button class="bg-grey reset" v-on:click="reset"> << Back</button>
        <span>!!! -------------------- Upload complete -------------------- !!!</span>
      </div>
      <div class="border-green margin-top" v-if="showSummary">
        <h4 class="flex no-margin small-padding border-green bg-green white align-center">
          Summary
        </h4>
        <table>
          <thead>
          <tr>
            <th>Total Payments</th>
            <th>Total payments with no amount ready for capture</th>
            <th>Total payments with amount ready for capture</th>
            <th class="red">Total errors</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>{{ uploadedData.length }}</td>
            <td>{{ noNeedCapture.length }}</td>
            <td>{{ needCapture.length }}</td>
            <td class="red">{{ errorData.length }}</td>
          </tr>
          </tbody>
        </table>
      </div>
      <template v-if="needCapture.length !== 0">
        <div class="border-dark-grey margin-top relative">
          <h4 class="no-margin small-padding bg-dark-grey white">Orders with an amount ready for capture</h4>
          <button class="smaller adjustment" v-on:click="makeAdjustment()">
            Adjust Dunning Fees and penny differences
          </button>
          <table>
            <thead>
            <tr>
              <th>Order ID</th>
              <th>Total value</th>
              <th>Outstanding balance</th>
              <th>Amount ready for capture</th>
              <th>&nbsp;</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="data in needCapture">
              <td><OrderLink :id="data.order_id" /></td>
              <td>{{ Number(data.order_total).toFixed(2) }}</td>
              <td>{{ Number(data.outstanding_balance).toFixed(2) }}</td>
              <td>{{ Number(data.amount_ready_for_capture).toFixed(2) }}</td>
              <td class="adjustment" v-html="data.message !== undefined ? data.message : ''"></td>
            </tr>
            </tbody>
          </table>
        </div>
      </template>
      <template v-if="errorData.length !== 0">
        <div class="border-red margin-top">
          <h4 class="no-margin small-padding bg-red white">Orders that have error in processing</h4>
          <table>
            <thead>
            <tr>
              <th>Order ID</th>
              <th>Message</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="data in errorData">
              <td><OrderLink :id="data.order_id" /></td>
              <td>{{ data.message }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'

export default {
  data: () => ({
    data: {},
    saving: false,
    form: {
      data: '',
      comment: ''
    },
    uploaded: false,
    showSummary: true,
    uploadedData: [],
    errorData: [],
    needCapture: [],
    noNeedCapture: []
  }),
  methods: {
    parseOrders() {
      if (!this.form.data) return []

      return this.form.data
        .trim()
        .split('\n')
        .map((line, index) => {
          const [orderId, amount] = line.split(';')
          if (orderId === undefined || amount === undefined) {
            alert("Error in input. \nPlease enter valid order ID and amount separated by semicolon")
            return;
          }
          return {
            orderId: orderId?.trim(),
            amount: amount?.trim()
          }
        })
    },
    submit () {
      const orders = this.parseOrders()
      orders.forEach(payment => {
        const data = {
          amount: payment.amount,
          mode: payment.amount > 0 ? 0 : 1,
          orderId: payment.orderId,
          reason: "Bulk upload",
          comment: this.form.comment,
          graceful_exception: true
        }
        Vue.$api.post('/bank-payment', data, {
          before: () => this.saving = true,
          callback: data => {
            data.order_id = payment.orderId
            this.uploadedData.push(data)
            this.handleSaving(orders)
          }
        })
      })
    },
    handleSaving(orders) {
      if (orders.length !== this.uploadedData.length) {
        return
      }
      this.saving = false
      this.uploaded = true
      this.uploadedData.forEach(data => {
        console.log(data);
        if (undefined !== data.exception) {
          this.errorData.push(data)
        } else if (data.amount_ready_for_capture !== undefined && data.amount_ready_for_capture !== 0) {
          this.needCapture.push(data)
        } else if (data.amount_ready_for_capture !== undefined && data.amount_ready_for_capture === 0) {
          this.noNeedCapture.push(data)
        } else {
          //
        }
      })
    },
    makeAdjustment() {
      this.needCapture.forEach(capture => {
        const data = {
          order_id: capture.orderId,
          graceful_exception: true
        };

        Vue.$api.post('/order/' + capture.order_id + '/adjust-payment', data, {
          before: () => {
            Vue.set(capture, 'message', '<img alt="loading......" style="width: 18px;" src="/img/loader.gif" />');
          },
          callback: data => {
            const color = data.adjustment !== undefined && data.adjustment > 0 ? 'green' : 'black'
            if (data.exception !== undefined) {
              Vue.set(capture, 'message', '<span class="red">' + data.message + '</span>');
            } else {
              Vue.set(capture, 'message', '<span class="' + color + '">' + data.message + '</span>');
              Vue.set(capture, 'outstanding_balance', data.outstanding_balance);
              Vue.set(capture, 'amount_ready_for_capture', data.amount_ready_for_capture);
            }
            console.log(capture);
          }
        });
      })
      this.showSummary = false
      this.errorData = []
    },
    reset () {
      this.uploadedData = []
      this.form.data = ''
      this.uploaded = null
      this.needCapture = []
      this.noNeedCapture = []
      this.errorData = []
    }
  }
}
</script>

<style scoped lang="less">
button.adjustment {
  position: absolute;
  top: 7px;
  right: 10px;
  background-color: white;
  color: black;
  cursor: pointer;
}
td.adjustment {
  width: 20%;
  font-weight: bold;
}
button.adjustment:hover {
  background-color: #ccc;
}
button.reset {
  position: absolute;
  left: 0;
  top: 5px;
}
.inline-loader {
  width: 10px;
}
</style>
